import {
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import {
  dayNumber,
  formatFullDay,
  formatMonthOf,
  formatWeekdayDay,
  isoDay,
} from "../format.js";
import {
  WEEKDAYS,
  isDayKey,
  isSameMonth,
  monthOf,
  shift,
  type DayKey,
} from "./grid.js";
import { parseTypedDate, typedForm } from "./typedDate.js";
import { DialogSurfacePortalContext } from "../ui/DialogSurfacePortalContext.js";

/**
 * A date, chosen from a calendar big enough to read.
 *
 * This exists because `<input type="date">` does not answer the one thing
 * asked of it: the browser's own picker is drawn by the browser, at the
 * browser's size, and no stylesheet can touch it. A grid of eighteen-pixel
 * numbers is the wrong size for choosing a reporting window or a school day,
 * and it was the only part of those pages that could not be made bigger.
 *
 * **Every date in the portal is chosen here.** One calendar, so that the diary,
 * the reports, the school day and the DBS form all behave the same way — a
 * portal with two different pickers depending on which page you are on teaches
 * people that neither can be relied on.
 *
 * So the calendar is ours. The arithmetic is the diary's — `monthOf` builds the
 * same six-by-seven grid the month view is drawn from, and `shift` steps whole
 * months without skipping February from the 31st — which means this is a
 * different size of the same calendar rather than a second implementation of
 * one.
 *
 * **Typing still works, because it has to.** Replacing the browser's field
 * means inheriting what it was good at, and one of those things was that
 * somebody who knows the date can type it. The box takes "12/9/26", "12 Sep
 * 2026" and the ISO form, and spells whatever it understood back out in words
 * the moment you leave it — see `typedDate.ts` for why day-first is read as
 * day-first and never guessed.
 *
 * **An empty date is a real value.** The reports treat a half-filled window as
 * asking for nothing rather than asking for everything, so clearing has to stay
 * possible; the browser's picker had a clear control and this one does too.
 */
export function DateField({
  disabled = false,
  label,
  min,
  onChange,
  value,
}: {
  /** Booking forms lock the date while a lookup or write is in flight. */
  disabled?: boolean;
  label: string;
  /** Days before this are shown but cannot be chosen. */
  min?: DayKey;
  onChange: (day: DayKey) => void;
  /** A day key, or "" for a date nobody has chosen. */
  value: DayKey;
}) {
  const [open, setOpen] = useState(false);
  /** Which month the calendar is showing, which is not the same as the date. */
  const [month, setMonth] = useState<DayKey>(() =>
    isDayKey(value) ? value : isoDay(0),
  );
  /**
   * What is in the box while somebody is typing in it, and null when nobody
   * is. Null rather than a copy of the date, so that the date changing
   * underneath — the Today button, the calendar, a reset elsewhere on the page
   * — reaches the box without anything having to keep the two in step.
   */
  const [typing, setTyping] = useState<string | null>(null);
  const dialogSurfaceRef = useContext(DialogSurfacePortalContext);
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const picker = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const labelId = useId();
  const pickerId = useId();
  const today = isoDay(0);
  const [pickerStyle, setPickerStyle] = useState<CSSProperties>({
    left: 8,
    top: 8,
  });

  /**
   * Closing.
   *
   * A popover that only closes by choosing a date is a trap — somebody who
   * opened the wrong one of two fields has no way out but to change it. So it
   * closes on Escape and on a click anywhere else, which are the two things
   * people try.
   */
  useEffect(() => {
    if (!open) return;

    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!box.current?.contains(target) && !picker.current?.contains(target))
        setOpen(false);
    };
    // Capture so a containing Dialog's bubble-phase mouse handler cannot hide
    // outside clicks from this nonmodal field popup.
    document.addEventListener("mousedown", onDown, true);
    // Escape is handled at the document edge as well as on the field, because
    // focus can be on the body or on non-focusable popup chrome after a click.
    // The capture-phase window listener runs before a containing Dialog's
    // document listener, so the popup closes first and the Dialog stays open.
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      // Whatever was half-typed goes with it: Escape means "leave it as it was".
      setTyping(null);
      window.requestAnimationFrame(() => opener.current?.focus());
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;

    const dialogSurface = dialogSurfaceRef?.current;
    const place = () => {
      const anchor = box.current?.getBoundingClientRect();
      const popup = picker.current;
      if (!anchor || !popup) return;

      const gutter = 8;
      const width = Math.min(330, window.innerWidth - gutter * 2);
      const measuredHeight = popup.getBoundingClientRect().height;
      const maxHeight = Math.max(240, window.innerHeight - gutter * 2);
      const height = Math.min(measuredHeight, maxHeight);
      const roomBelow = window.innerHeight - anchor.bottom - gutter;
      const roomAbove = anchor.top - gutter;
      const openAbove = roomBelow < height && roomAbove > roomBelow;
      const preferredTop = openAbove
        ? anchor.top - height - 6
        : anchor.bottom + 6;
      const top = Math.max(
        gutter,
        Math.min(preferredTop, window.innerHeight - height - gutter),
      );
      const left = Math.max(
        gutter,
        Math.min(anchor.left, window.innerWidth - width - gutter),
      );

      setPickerStyle({ left, top, width, maxHeight });
    };

    place();
    window.addEventListener("resize", place);
    if (dialogSurface) dialogSurface.addEventListener("scroll", place, true);
    else window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      if (dialogSurface) dialogSurface.removeEventListener("scroll", place, true);
      else window.removeEventListener("scroll", place, true);
    };
  }, [open, month, dialogSurfaceRef]);

  /** Opening always lands on the month of the date held, not where it was left. */
  const show = (next: boolean, openedBy?: HTMLElement) => {
    if (next && openedBy) opener.current = openedBy;
    if (next) setMonth(isDayKey(value) ? value : isoDay(0));
    setOpen(next);
  };

  const closeOnEscape = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape") return;
    if (!open) {
      if (typing !== null) setTyping(null);
      return;
    }
    // Close this field popup first. Stopping propagation keeps a containing
    // Dialog open, while the field remains an ordinary nonmodal popup.
    event.preventDefault();
    event.stopPropagation();
    setOpen(false);
    setTyping(null);
    window.requestAnimationFrame(() => opener.current?.focus());
  };

  const choose = (day: DayKey) => {
    onChange(day);
    setTyping(null);
    setOpen(false);
    window.requestAnimationFrame(() =>
      (opener.current ?? input.current)?.focus(),
    );
  };

  const focusCalendarDay = (index: number) => {
    const bounded = Math.max(0, Math.min(days.length - 1, index));
    picker.current
      ?.querySelector<HTMLButtonElement>(`[data-date-index="${bounded}"]`)
      ?.focus();
  };

  /**
   * What the box says.
   *
   * Spelled out in words when nobody is in it — "Friday 5 June 2026" — and
   * short while it is being typed. The long form is what makes typing safe:
   * a slash-separated date read the wrong way round is a different day, and
   * the words say which one was understood before anything is run on it.
   */
  const inBox = typing ?? (isDayKey(value) ? formatWeekdayDay(value) : "");

  const type = (text: string) => {
    setTyping(text);
    if (text.trim() === "") {
      onChange("");
      return;
    }
    const parsed = parseTypedDate(text, Number(month.slice(0, 4)));
    if (parsed === null) return;
    onChange(parsed);
    // The calendar follows the typing, so the two halves of the field never
    // disagree about which month is being talked about.
    setMonth(parsed);
  };

  const days = monthOf(month);
  const portalTarget = dialogSurfaceRef?.current ??
    (typeof document === "undefined" ? null : document.body);
  const inDialog = Boolean(dialogSurfaceRef?.current && portalTarget === dialogSurfaceRef.current);

  return (
    <div className="datefield" ref={box} onKeyDown={closeOnEscape}>
      <span className="small muted" id={labelId}>
        {label}
      </span>

      <div className="datefield-box">
        <input
          ref={input}
          aria-invalid={min && isDayKey(value) && value < min ? true : undefined}
          aria-labelledby={labelId}
          className="datefield-input"
          disabled={disabled}
          onBlur={() => setTyping(null)}
          onChange={(event) => type(event.target.value)}
          // Both, and not only focus: pressing Enter closes the calendar
          // without moving focus out of the box, so a second click on the same
          // field fires no focus event and would otherwise do nothing at all.
          onClick={(event) => show(true, event.currentTarget)}
          onFocus={(event) => {
            setTyping(typedForm(value));
            // Selected, so that typing over it replaces the date rather than
            // landing in the middle of it.
            event.target.select();
            show(true, event.currentTarget);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              // A form around this — the DBS one — would otherwise submit on
              // the keystroke that was meant to finish the date.
              event.preventDefault();
              setTyping(null);
              setOpen(false);
            }
          }}
          placeholder="dd/mm/yyyy"
          value={inBox}
        />
        <button
          aria-expanded={open}
          aria-controls={open ? pickerId : undefined}
          aria-haspopup="dialog"
          aria-label={`Choose ${label} from a calendar`}
          className="datefield-open"
          disabled={disabled}
          onClick={(event) => show(!open, event.currentTarget)}
          type="button"
        >
          <CalendarIcon />
        </button>
      </div>

      {open && portalTarget &&
        createPortal(
          <div
            ref={picker}
            id={pickerId}
            aria-label={`Choose ${label}`}
            className={inDialog ? "datepicker datepicker--in-dialog" : "datepicker"}
            role="dialog"
            style={pickerStyle}
          >
            <div className="datepicker-bar">
              <button
                aria-label="Previous month"
                className="action-tertiary"
                onClick={() => setMonth(shift("month", month, -1))}
                type="button"
              >
                ←
              </button>
              <strong aria-live="polite">{formatMonthOf(month)}</strong>
              <button
                aria-label="Next month"
                className="action-tertiary"
                onClick={() => setMonth(shift("month", month, 1))}
                type="button"
              >
                →
              </button>
            </div>

            <div className="datepicker-grid">
              {WEEKDAYS.map((weekday) => (
                <div className="datepicker-weekday" key={weekday}>
                  {weekday}
                </div>
              ))}

              {days.map((day, index) => {
                const classes = ["datepicker-day"];
                // The days borrowed from the months either side stay choosable —
                // dimmed rather than disabled, because the 1st of next month is
                // a perfectly ordinary end of a reporting window.
                if (!isSameMonth(day, month)) classes.push("is-outside");
                if (day === today) classes.push("is-today");
                if (day === value) classes.push("is-chosen");

                return (
                  <button
                    aria-current={day === value ? "date" : undefined}
                    className={classes.join(" ")}
                    data-date-index={index}
                    disabled={min !== undefined && day < min}
                    key={day}
                    onClick={() => choose(day)}
                    onKeyDown={(event) => {
                      const movement = {
                        ArrowLeft: -1,
                        ArrowRight: 1,
                        ArrowUp: -7,
                        ArrowDown: 7,
                      }[event.key];
                      if (movement === undefined) return;
                      event.preventDefault();
                      focusCalendarDay(index + movement);
                    }}
                    type="button"
                  >
                    <span aria-hidden="true">{dayNumber(day)}</span>
                    <span className="visually-hidden">
                      {formatFullDay(day)}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="datepicker-bar is-footer">
              <button
                className="action-tertiary small"
                disabled={min !== undefined && today < min}
                onClick={() => choose(today)}
                type="button"
              >
                Today
              </button>
              <button
                className="action-tertiary small"
                onClick={() => {
                  onChange("");
                  setTyping(null);
                  setOpen(false);
                  window.requestAnimationFrame(() =>
                    (opener.current ?? input.current)?.focus(),
                  );
                }}
                type="button"
              >
                Clear
              </button>
            </div>
          </div>,
          portalTarget,
        )}
    </div>
  );
}

/**
 * The calendar on the button that opens it.
 *
 * Drawn rather than fetched or set in a font: the portal loads no icon set and
 * is not going to gain one for a single glyph, and an emoji renders as a
 * different picture on every machine that opens the page.
 */
function CalendarIcon() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="16"
      viewBox="0 0 16 16"
      width="16"
    >
      <rect
        height="11"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.4"
        width="13"
        x="1.5"
        y="3.5"
      />
      <path d="M1.5 6.5h13" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M5 1.5v3M11 1.5v3"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.4"
      />
    </svg>
  );
}
